import { nectarApi } from '@/features/nectar/api/client'
import { type UseQueryResult, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'

export type TremendousProduct = {
  id: string
  name: string
  logoUrl: string | null
  countries: string[]
  currency: string
} & (
  | {
      kind: 'range'
      range: { min: string; max: string; minUsd: string | null; maxUsd: string | null }
      fixed: null
    }
  | { kind: 'fixed'; range: null; fixed: { amount: string; usd: string | null }[] }
)

export type TremendousOptions = { products: TremendousProduct[] }

// NATIVE: an amount in the product's own currency. USD: exactly two decimals.
// Neither is the payout-methods `MONEY_RE`, which fits neither.
export const NATIVE_RE = /^(0|[1-9]\d*)(\.\d+)?$/
export const USD_RE = /^\d+\.\d{2}$/
export const CURRENCY_RE = /^[A-Z]{3}$/

export const TREMENDOUS_NOT_AVAILABLE = 'TREMENDOUS_NOT_AVAILABLE'
export const TREMENDOUS_OPTIONS_LOADING = 'TREMENDOUS_OPTIONS_LOADING'
export const TREMENDOUS_OPTIONS_UNAVAILABLE = 'TREMENDOUS_OPTIONS_UNAVAILABLE'

export const TREMENDOUS_OPTIONS_KEY = ['tremendous-options']
export const OPTIONS_STALE_MS = 300_000
export const LOADING_RETRY_DELAY_MS = 3_000
export const LOADING_MAX_RETRIES = 15

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null

const isNative = (v: unknown): v is string => typeof v === 'string' && NATIVE_RE.test(v)
const isUsdOrNull = (v: unknown): v is string | null => v === null || (typeof v === 'string' && USD_RE.test(v))

// Returns the parsed product, or a string naming why it is dropped.
const readProduct = (raw: unknown): TremendousProduct | string => {
  if (!isObject(raw)) return 'not an object'
  const { id, name, logoUrl, countries, currency, kind, range, fixed } = raw
  if (typeof id !== 'string') return 'id is not a string'
  if (typeof name !== 'string') return 'name is not a string'
  if (logoUrl !== null && typeof logoUrl !== 'string') return 'logoUrl is not a string or null'
  if (!Array.isArray(countries) || countries.length === 0 || !countries.every((c) => typeof c === 'string')) {
    return 'countries is not a non-empty string array'
  }
  if (typeof currency !== 'string' || !CURRENCY_RE.test(currency)) return 'currency is not 3 uppercase letters'
  const head = { id, name, logoUrl, countries: [...(countries as string[])], currency }

  if (kind === 'range') {
    if (fixed !== null) return 'kind range with fixed set'
    if (!isObject(range)) return 'kind range without range'
    if (!isNative(range.min) || !isNative(range.max)) return 'range.min/max is not a NATIVE amount'
    if (!isUsdOrNull(range.minUsd) || !isUsdOrNull(range.maxUsd)) return 'range.minUsd/maxUsd is not USD or null'
    if ((range.minUsd === null) !== (range.maxUsd === null)) return 'range USD edges are not null together'
    return {
      ...head,
      kind: 'range',
      range: { min: range.min, max: range.max, minUsd: range.minUsd, maxUsd: range.maxUsd },
      fixed: null
    }
  }

  if (kind === 'fixed') {
    if (range !== null) return 'kind fixed with range set'
    if (!Array.isArray(fixed) || fixed.length === 0) return 'kind fixed without fixed amounts'
    const out: { amount: string; usd: string | null }[] = []
    for (const f of fixed as unknown[]) {
      if (!isObject(f)) return 'fixed entry is not an object'
      if (!isNative(f.amount)) return 'fixed amount is not a NATIVE amount'
      if (!isUsdOrNull(f.usd)) return 'fixed usd is not USD or null'
      out.push({ amount: f.amount, usd: f.usd })
    }
    if (out.some((f) => f.usd === null) !== out.every((f) => f.usd === null)) {
      return 'fixed USD values are not null together'
    }
    return { ...head, kind: 'fixed', range: null, fixed: out }
  }

  return 'unknown kind'
}

// Unknown keys are ignored; a product that breaks a rule is dropped with a warning;
// a malformed body or zero surviving products throws.
export const parseTremendousOptions = (raw: unknown): TremendousOptions => {
  if (!isObject(raw)) throw new Error('parseTremendousOptions: body is not an object')
  if (typeof raw.computedAt !== 'string') throw new Error('parseTremendousOptions: computedAt is not a string')
  if (!Array.isArray(raw.products)) throw new Error('parseTremendousOptions: products is not an array')
  const products: TremendousProduct[] = []
  for (const p of raw.products as unknown[]) {
    const result = readProduct(p)
    if (typeof result === 'string') {
      console.warn('parseTremendousOptions: dropped product', isObject(p) ? p.id : undefined, result)
    } else {
      products.push(result)
    }
  }
  if (products.length === 0) throw new Error('parseTremendousOptions: no usable products')
  return { products }
}

export const tremendousOptionsErrorCode = (err: unknown): string | undefined => {
  if (!axios.isAxiosError(err)) return undefined
  const code: unknown = err.response?.data?.error?.code
  return typeof code === 'string' ? code : undefined
}

export const useFetchTremendousOptions = (): UseQueryResult<TremendousOptions> => {
  const queryClient = useQueryClient()
  return useQuery({
    queryKey: TREMENDOUS_OPTIONS_KEY,
    queryFn: async (): Promise<TremendousOptions> => {
      try {
        return parseTremendousOptions(await nectarApi.get<unknown>('/v1/user/payout-methods/tremendous/options'))
      } catch (err) {
        if (tremendousOptionsErrorCode(err) === TREMENDOUS_NOT_AVAILABLE) {
          void queryClient.invalidateQueries({ queryKey: ['payout-methods'] })
        }
        throw err
      }
    },
    retry: (failureCount, err) =>
      failureCount < LOADING_MAX_RETRIES && tremendousOptionsErrorCode(err) === TREMENDOUS_OPTIONS_LOADING,
    retryDelay: LOADING_RETRY_DELAY_MS,
    staleTime: OPTIONS_STALE_MS
  })
}
